import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface PinsData {
  parks: number;
  delivery: number;
}

export function PinsCounter() {
  const [pinsData, setPinsData] = useState<PinsData | null>(null);
  const [loading, setLoading] = useState(true);
  const retryCountRef = useRef(0);

  useEffect(() => {
    let retryTimeout: NodeJS.Timeout | null = null;

    const loadPinsData = async (isRetry = false) => {
      try {
        if (!isRetry) {
          setLoading(true);
          retryCountRef.current = 0;
        }

        // Verificar se o Supabase está disponível
        if (!supabase) {
          throw new Error('Supabase client não disponível');
        }
        
        // Carregar dados iniciais da view
        const { data, error: fetchError } = await supabase
          .from('pins')
          .select('*')
          .single();

        if (fetchError) {
          console.error('❌ Erro ao carregar pins:', fetchError);
          // Se for erro de rede ou timeout, tentar novamente
          if (fetchError.message.includes('fetch') || fetchError.message.includes('network') || fetchError.message.includes('timeout')) {
            if (retryCountRef.current < 3) {
              const delay = (retryCountRef.current + 1) * 2000; // 2s, 4s, 6s
              console.log(`🔄 Tentando novamente em ${delay}ms... (tentativa ${retryCountRef.current + 1}/3)`);
              retryCountRef.current += 1;
              retryTimeout = setTimeout(() => {
                loadPinsData(true);
              }, delay);
              return;
            }
          }
          throw new Error(`Erro ao carregar dados: ${fetchError.message}`);
        }

        setPinsData(data);
        setLoading(false);
        retryCountRef.current = 0;

        // Poll the pins view (SUM(quantity) where tx_hash IS NOT NULL).
        // Do not subscribe to purchases: anon must not SELECT PII rows.

      } catch (err) {
        console.error('❌ Erro ao carregar dados dos pins:', err);
        // Tentar novamente se ainda não tentou 3 vezes
        if (retryCountRef.current < 3) {
          const delay = (retryCountRef.current + 1) * 2000;
          console.log(`🔄 Tentando novamente em ${delay}ms... (tentativa ${retryCountRef.current + 1}/3)`);
          retryCountRef.current += 1;
          retryTimeout = setTimeout(() => {
            loadPinsData(true);
          }, delay);
        } else {
          // Após 3 tentativas, mostrar 0 ao invés de ERR
          console.warn('⚠️ Não foi possível carregar dados após 3 tentativas, mostrando 0');
          setPinsData({ parks: 0, delivery: 0 });
          setLoading(false);
        }
      }
    };

    const refreshPinsData = async () => {
      try {
        // const supabase = await sb(); // já temos o singleton
        const { data, error: refreshError } = await supabase
          .from('pins')
          .select('*')
          .single();

        if (refreshError) {
          console.error('❌ Erro ao recarregar dados:', refreshError);
          return;
        }

        setPinsData(data);
        console.log('✅ Dados dos pins atualizados:', data);
      } catch (err) {
        console.error('❌ Erro ao recarregar dados dos pins:', err);
      }
    };

    loadPinsData();
    const poll = setInterval(() => {
      refreshPinsData();
    }, 10000);

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      clearInterval(poll);
    };
  }, []);

  if (loading) {
    return (
      <span style={{ color: '#f9d70b', fontSize: '1.2rem', fontFamily: 'Arial Black, sans-serif' }}>
        ...
      </span>
    );
  }

  // Não mostrar ERR, apenas continuar tentando ou mostrar 0
  // if (error) {
  //   return (
  //     <span style={{ color: '#f9d70b', fontSize: '1.2rem', fontFamily: 'Arial Black, sans-serif' }}>
  //       ERR
  //     </span>
  //   );
  // }

  if (!pinsData) {
    return null;
  }

  return (
    <span style={{ color: '#f9d70b', fontSize: '1.2rem', fontFamily: 'Arial Black, sans-serif' }}>
      {pinsData.parks}
    </span>
  );
}
